import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { School, Plus, Loader2, Search } from 'lucide-react';

import { ClassCard } from '@/components/admin/classes/ClassCard';
import { ClassFormDialog } from '@/components/admin/classes/ClassFormDialog';
import { ClassMessageDialog } from '@/components/admin/classes/ClassMessageDialog';
import { logError } from '@/lib/logger';

import { API } from '@/lib/apiBase';

export default function AdminClasses() {
  const { getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [formData, setFormData] = useState({
    name: '', year: new Date().getFullYear(), school_name: ''
  });

  // Notify-parents dialog state
  const [messageClass, setMessageClass] = useState(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API}/classes`, {
        headers: getAuthHeaders(), credentials: 'include'
      });
      if (response.ok) setClasses(await response.json());
    } catch (error) {
      logError('AdminClasses.Errorfetchingclasses', error);
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClasses(); }, [getAuthHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingClass ? `${API}/classes/${editingClass.class_id}` : `${API}/classes`;
      const method = editingClass ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Failed');
      toast.success(editingClass ? t('admin.classUpdated') : t('admin.classCreated'));
      setDialogOpen(false);
      setEditingClass(null);
      setFormData({ name: '', year: new Date().getFullYear(), school_name: '' });
      fetchClasses();
    } catch (error) {
      logError('AdminClasses.Errorsavingclass', error);
      toast.error(t('common.saveFailed'));
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm(t('admin.classDeleteConfirm'))) return;
    try {
      const response = await fetch(`${API}/classes/${classId}`, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed');
      toast.success(t('admin.classDeleted'));
      fetchClasses();
    } catch (error) {
      logError('AdminClasses.Errordeletingclass', error);
      toast.error(t('common.deleteFailed'));
    }
  };

  const openEditDialog = (cls) => {
    setEditingClass(cls);
    setFormData({ name: cls.name, year: cls.year, school_name: cls.school_name });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingClass(null);
    setFormData({ name: '', year: new Date().getFullYear(), school_name: '' });
    setDialogOpen(true);
  };

  const handleCopyCode = (cls) => {
    navigator.clipboard.writeText(cls.teacher_code);
    setCopiedCode(cls.class_id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const openMessageDialog = async (cls) => {
    setMessageClass(cls);
    setMessageSubject('');
    setMessageBody('');
    setRecipientPreview(null);
    try {
      const res = await fetch(`${API}/admin/classes/${cls.class_id}/parent-recipients`, {
        headers: getAuthHeaders(), credentials: 'include'
      });
      if (res.ok) setRecipientPreview(await res.json());
    } catch (e) { logError('AdminClasses.recipientPreview', e); }
  };

  const sendClassMessage = async () => {
    if (!messageClass) return;
    if (!messageSubject.trim() || !messageBody.trim()) {
      toast.error(t('admin.messageRequired'));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/classes/${messageClass.class_id}/notify-parents`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          subject: messageSubject.trim(),
          message: messageBody.trim(),
          lang: language || 'de',
        }),
      });
      if (!res.ok) throw new Error('Failed');
      const result = await res.json();
      if (result.recipient_count === 0) {
        toast.info(t('admin.messageNoRecipients'));
      } else {
        toast.success(`${result.emails_sent} / ${result.recipient_count} ${t('admin.messageSent')}`);
      }
      setMessageClass(null);
    } catch (e) {
      logError('AdminClasses.sendMessage', e);
      toast.error(t('common.error'));
    } finally {
      setSending(false);
    }
  };

  const filteredClasses = useMemo(() => {
    if (!searchQuery.trim()) return classes;
    const q = searchQuery.toLowerCase();
    return classes.filter((cls) =>
      cls.name.toLowerCase().includes(q) ||
      cls.school_name.toLowerCase().includes(q) ||
      String(cls.year).includes(q)
    );
  }, [classes, searchQuery]);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12" data-testid="admin-classes">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">Manage Classes</h1>
          <p className="text-muted-foreground">Create and manage school classes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="pl-9 w-[180px] bg-card border-border"
              data-testid="class-search-input"
            />
          </div>
          <Button onClick={openCreateDialog} className="bg-primary hover:bg-primary/90" data-testid="add-class-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Class
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : classes.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <School className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-bold mb-2">No classes yet</h3>
          <p className="text-muted-foreground mb-6">Create your first class to get started</p>
          <Button onClick={openCreateDialog} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" />
            Create Class
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClasses.map((cls, index) => (
            <ClassCard
              key={cls.class_id}
              cls={cls}
              index={index}
              copiedCode={copiedCode}
              onCopyCode={handleCopyCode}
              onMessage={openMessageDialog}
              onEdit={openEditDialog}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <ClassFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingClass={editingClass}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
      />

      <ClassMessageDialog
        messageClass={messageClass}
        onClose={() => setMessageClass(null)}
        messageSubject={messageSubject}
        setMessageSubject={setMessageSubject}
        messageBody={messageBody}
        setMessageBody={setMessageBody}
        recipientPreview={recipientPreview}
        sending={sending}
        onSend={sendClassMessage}
      />
    </div>
  );
}
